/**
 * The Bell Below — Deepwake's story arc. A Vigil correspondent writes to you
 * as you fly. Progress is tracked server-side from what you actually do;
 * chapters arrive as cinematic interludes. All original.
 */

export type StoryTrigger =
  | { kind: 'warp' } // any warp
  | { kind: 'killUnbound'; n: number }
  | { kind: 'mine'; n: number }
  | { kind: 'visit'; systemId: number }
  | { kind: 'killDeep'; n: number };

export interface StoryChapter {
  ch: number;
  title: string;
  body: string;
  objective: string;
  trigger: StoryTrigger;
  rewardMarks: number;
  rewardItems?: Record<string, number>;
}

export const STORY: StoryChapter[] = [
  {
    ch: 1,
    title: 'Wake',
    body: `A message rides in on your core's wake-cycle, unsigned but sealed with the Vigil's bell-and-eye.

"Captain. Forgive the intrusion — your core is stasis-era, and stasis-era cores talk in their sleep. Ours listen. Mine heard yours wake three days before your foundry printed you a hull, and it woke saying a name I have spent forty years listening for.

I will not waste your time with mysticism. Fly. Warp somewhere — anywhere. Your core sings loudest in the tunnel, and I need to hear the note it holds.

— Sister Halex, Listener Second Class, Vigilkeep"`,
    objective: 'Warp anywhere.',
    trigger: { kind: 'warp' },
    rewardMarks: 500,
  },
  {
    ch: 2,
    title: "The Ferryman's Toll",
    body: `"There. In the tunnel. You heard it too — don't pretend otherwise; your core's telemetry blushed.

The note your core holds is a bell-tone. Warden gates ring it when they wake. Cores do not. Cores CANNOT, captain, and yet.

I need to know if the tone survives violence. The Unbound work the drifts out past the Watch line, and they will oblige you a fight without being asked twice. Put two of their hulls down and let your core sing through the smoke.

The Compact will call this a mercenary errand. Let them. Every instrument must be struck to be tuned."`,
    objective: 'Destroy 2 Unbound hulls (the Verge or the Deep).',
    trigger: { kind: 'killUnbound', n: 2 },
    rewardMarks: 1_500,
  },
  {
    ch: 3,
    title: 'Ore That Sings',
    body: `"The tone survived. It DEEPENED. My superiors are now officially interested, which for the Vigil is a state of emergency.

Here is what four generations of listening bought us: the bell-tone lives in matter, not machinery. It sleeps in ore the way your pattern sleeps in your core. Stillore carries it strongest, but any rock that has drifted long enough remembers a little of the song.

Mine, captain. Pull ore until your hold hums with it. My instruments will do the rest from here.

(Also — and this stays between us — sell the surplus. The Reach's economists insist the bell-tone is not collateral. Their loss.)"`,
    objective: 'Mine 300 units of ore.',
    trigger: { kind: 'mine', n: 300 },
    rewardMarks: 2_500,
  },
  {
    ch: 4,
    title: 'The Silent Ferry',
    body: `"I have not slept. Your ore-song matched a recording, captain — one archived the year of the Stilling itself, logged by a listening post that no longer exists, in a system the charts have spent four generations pretending is empty.

Silent Ferry. The Deep. The post's last transmission was eleven words: 'The gate is not dead. It is holding its breath. Listen.'

The Watch will not escort you. The Compact will not insure you. The Unbound consider that space theirs, and they are not wrong. Go anyway. Get to Silent Ferry and let your core listen where mine cannot.

Bring guns. Bring patience. Bring yourself back."`,
    objective: 'Travel to the Silent Ferry system, in the Deep.',
    trigger: { kind: 'visit', systemId: 121 },
    rewardMarks: 4_000,
  },
  {
    ch: 5,
    title: 'The Bell Below',
    body: `"Your core's telemetry from Silent Ferry reached me at 0340 station-time and I have been sitting with it since, the way you sit with a letter you already know the contents of.

It is not one bell, captain. It never was. Every gate, every fragment, every core, every vein of stillore — they are all one instrument, struck once, still ringing. The Stilling was not the song stopping.

It was the intake of breath.

The Unbound crews holding Silent Ferry have been salvaging the old listening post. They do not know what they are carrying. Put three of their Deep hulls down — I will not risk what happens if that cargo reaches a breaker yard — and the Vigil will owe you what the Vigil least likes to spend: gatesong, and the truth, as we learn it.

The gates are waking, captain. Something is waking them. Keep flying.

— H."`,
    objective: 'Destroy 3 Unbound hulls in the Deep.',
    trigger: { kind: 'killDeep', n: 3 },
    rewardMarks: 9_000,
    rewardItems: { gatesong: 25 },
  },
];

export const INTRO_SLIDES: { title: string; body: string }[] = [
  {
    title: 'THE LATTICE',
    body: 'Humanity crossed the stars through gates it did not build and could not repair — a borrowed network, four hundred years wide, hung between the systems by hands no one ever saw.',
  },
  {
    title: 'THE STILLING',
    body: 'Between one breath and the next, every gate closed like an eye. Ships in transit never arrived. A thousand worlds went dark alone. Four generations kept ledgers of everything that was lost.',
  },
  {
    title: 'THE REACH',
    body: 'One cluster survived out of spite. And thirty-one years ago, without pattern or warning, its gates began to wake — one door at a time, redrawing every map, every treaty, every debt.',
  },
  {
    title: 'YOU ARE A WAKER',
    body: 'Your ship is printed around a stasis-era core that survives when hulls do not. You lose the metal. You keep the self. The Reach is open, the drifts are singing, and nobody knows which gate wakes next.',
  },
];
